import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import * as Fonts from "../../../../components/Fonts";
import { LoadingModal } from "../../../../components/LoadingModal";
import { SubHeader } from "../../../../components/SubHeader";
import constants from "../../../../constants";
import { api } from "../../../../services/api";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { formatBRL, toNumber, YEARLY_SAVING } from "../../pricing";
import { PlanCard, Plans } from "../Features/styles";
import { Actions, ChangeSummary, Container, Section } from "./styles";
import { useManagedSubscription, useSave } from "./useManagedSubscription";

type Plan = "MONTHLY" | "ANNUALLY";

/**
 * Monthly ↔ yearly. Going yearly charges today, minus what's left of the month already paid; going monthly
 * waits for the paid year to end (nothing is refunded).
 */
export const ChangePlanPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.subscription.manage.changePlan";
  const { detail, isAnnual, isLoading, detailUrl, siteId } = useManagedSubscription();
  const { state, error, save } = useSave(detailUrl, navigate);

  const current: Plan = isAnnual ? "ANNUALLY" : "MONTHLY";
  const [target, setTarget] = useState<Plan>(isAnnual ? "MONTHLY" : "ANNUALLY");
  // What Stripe will charge today for going yearly (its own proration); until it answers, the estimate below
  const [previewedCharge, setPreviewedCharge] = useState<{ plan: Plan; value: number }>();

  // The current plan is known only once loaded: start on the other one
  useEffect(() => {
    if (!isLoading) {
      setTarget(isAnnual ? "MONTHLY" : "ANNUALLY");
    }
  }, [isAnnual, isLoading]);

  useEffect(() => {
    // Only going yearly is charged today
    if (isLoading || target !== "ANNUALLY" || isAnnual) {
      return;
    }
    let cancelled = false;
    api
      .previewPlanChange(siteId, target)
      .then(preview => !cancelled && setPreviewedCharge({ plan: target, value: preview.amount / 100 }))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAnnual, isLoading, siteId, target]);

  if (isLoading) {
    return <LoadingModal />;
  }

  const format = (date: dayjs.Dayjs) => formatLocaleDate(date, i18n.language);
  const periodEnd = dayjs(detail?.currentPeriodEnd);
  const isSame = target === current;

  const yearly = toNumber(constants.price.year.value);
  const monthly = toNumber(constants.price.month.value);
  const paid = detail?.plan ? detail.plan.amount / 100 : monthly;
  // What's left of the month already paid comes off the yearly charge
  const daysLeft = Math.max(0, periodEnd.diff(dayjs(), "day"));
  const estimatedCredit = Math.round(((paid * daysLeft) / 30) * 100) / 100;
  const chargeToday =
    previewedCharge?.plan === "ANNUALLY" ? previewedCharge.value : Math.max(0, yearly - estimatedCredit);
  const credit = Math.max(0, Math.round((yearly - chargeToday) * 100) / 100);

  const planTitle = (plan: Plan) =>
    t(`pages.subscription.features.plans.${plan === "ANNUALLY" ? "annually" : "monthly"}.title`);

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[
          { label: t("pages.plusBenefits.title"), to: "/keepo/plus" },
          { label: t("pages.subscriptionDetail.title"), to: detailUrl },
        ]}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.choose`)}</Fonts.Label>
        <Plans role="radiogroup" aria-label={t(`${prefix}.choose`)} style={{ maxWidth: "none", marginTop: 0 }}>
          <PlanCard
            type="button"
            role="radio"
            aria-checked={target === "ANNUALLY"}
            $selected={target === "ANNUALLY"}
            onClick={() => setTarget("ANNUALLY")}>
            <span className="save">
              {current === "ANNUALLY"
                ? t(`${prefix}.current`)
                : t("pages.subscription.features.page.plans.save", { value: YEARLY_SAVING })}
            </span>
            <span className="radio" aria-hidden="true" />
            <span className="name">
              <b>
                {planTitle("ANNUALLY")}
                <span className="discount">{constants.price.year.discount}</span>
              </b>
              <span>{t("pages.subscription.features.page.plans.yearly", { value: constants.price.year.value })}</span>
            </span>
            <span className="value">
              {constants.price.year.perMonth}
              <small>{t("pages.subscription.features.plans.perMonth")}</small>
            </span>
          </PlanCard>
          <PlanCard
            type="button"
            role="radio"
            aria-checked={target === "MONTHLY"}
            $selected={target === "MONTHLY"}
            onClick={() => setTarget("MONTHLY")}>
            {current === "MONTHLY" && <span className="save">{t(`${prefix}.current`)}</span>}
            <span className="radio" aria-hidden="true" />
            <span className="name">
              <b>{planTitle("MONTHLY")}</b>
              <span>{t("pages.subscription.features.page.plans.monthly")}</span>
            </span>
            <span className="value">
              {constants.price.month.value}
              <small>{t("pages.subscription.features.plans.perMonth")}</small>
            </span>
          </PlanCard>
        </Plans>
      </Section>

      {!isSame && (
        <Section>
          <Fonts.Label>{t(`${prefix}.whatChanges`)}</Fonts.Label>
          {target === "ANNUALLY" ? (
            <ChangeSummary>
              <div>
                <dt>{t(`${prefix}.toYearly.plan`)}</dt>
                <dd>{constants.price.year.value}</dd>
              </div>
              {credit > 0 && (
                <div className="credit">
                  <dt>{t(`${prefix}.toYearly.credit`, { count: daysLeft })}</dt>
                  <dd>− {formatBRL(credit)}</dd>
                </div>
              )}
              <div className="total">
                <dt>{t(`${prefix}.toYearly.today`)}</dt>
                <dd>{formatBRL(chargeToday)}</dd>
              </div>
              <span className="note">
                {t(`${prefix}.toYearly.note`, {
                  date: format(dayjs().add(1, "year")),
                  value: constants.price.year.value,
                })}
              </span>
            </ChangeSummary>
          ) : (
            <ChangeSummary>
              <div>
                <dt>{t(`${prefix}.toMonthly.until`, { date: format(periodEnd) })}</dt>
                <dd>{planTitle("ANNUALLY")}</dd>
              </div>
              <div>
                <dt>{t(`${prefix}.toMonthly.from`, { date: format(periodEnd) })}</dt>
                <dd>
                  {constants.price.month.value}
                  {t("pages.subscription.features.plans.perMonth")}
                </dd>
              </div>
              <div className="total">
                <dt>{t(`${prefix}.toMonthly.today`)}</dt>
                <dd>{formatBRL(0)}</dd>
              </div>
              <span className="note">{t(`${prefix}.toMonthly.note`)}</span>
            </ChangeSummary>
          )}
        </Section>
      )}

      <Actions>
        {error && (
          <p className="error" role="alert">
            {t(`pages.subscription.manage.errors.${error}`)}
          </p>
        )}
        {state === "done" ? (
          <p className="done">{t(`${prefix}.saved`, { plan: planTitle(target) })}</p>
        ) : (
          <button
            type="button"
            className="primary"
            disabled={isSame || state === "saving"}
            aria-busy={state === "saving"}
            onClick={() => save(() => api.changePlan(siteId, target))}>
            {isSame
              ? t(`${prefix}.isCurrent`)
              : target === "ANNUALLY"
                ? t(`${prefix}.toYearly.confirm`, { value: formatBRL(chargeToday) })
                : t(`${prefix}.toMonthly.confirm`)}
          </button>
        )}
        <button type="button" className="secondary" onClick={() => navigate(detailUrl)}>
          {t("pages.subscription.manage.cancel")}
        </button>
      </Actions>
    </Container>
  );
};
